import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Key, Activity, Clock, Database,
  LogOut, ChevronRight, ChevronLeft, AlertCircle, CheckCircle2, XCircle,
  Shield, Copy, Check, Plus, Trash2, RefreshCw,
  Settings, Eye, EyeOff, Search, MoreVertical,
  FileText
} from 'lucide-react';
import BloodFlowLogo from '../../Assets/BloodflowLogoNoLetters.png';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

// Particle background
const ParticleField = () => {
  const particles = useMemo(() => 
    [...Array(25)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2,
      duration: 3 + Math.random() * 4,
      delay: Math.random() * 2,
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-red-500"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size }}
          animate={{ opacity: [0, 0.4, 0], scale: [0, 1, 0], y: [0, -80] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeOut" }}
        />
      ))}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color = 'red' }) => {
  const colorClasses = {
    red: 'from-red-500/20 to-red-600/5 border-red-500/20',
    green: 'from-green-500/20 to-green-600/5 border-green-500/20',
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/20',
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/20',
    purple: 'from-purple-500/20 to-purple-600/5 border-purple-500/20',
  };

  const iconColors = {
    red: 'text-red-400 bg-red-500/10',
    green: 'text-green-400 bg-green-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${colorClasses[color]} backdrop-blur-xl rounded-2xl p-5 border`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-xl ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <h3 className="text-gray-400 text-xs font-medium mb-1">{title}</h3>
      <p className="text-white text-xl font-bold">{value}</p>
      {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
    </motion.div>
  );
};

// API Key Row Component
const ApiKeyRow = ({ apiKey, onToggle, onReset, onUpdateLimit, onDelete, onViewLogs }) => {
  const [showActions, setShowActions] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [newLimit, setNewLimit] = useState(apiKey.tokenLimit);

  const usageColor = apiKey.usagePercentage > 80 ? 'text-red-400' : 
                     apiKey.usagePercentage > 50 ? 'text-amber-400' : 'text-green-400';

  return (
    <>
      <motion.tr 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border-b border-white/5 hover:bg-white/5 transition-colors"
      >
        <td className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${apiKey.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
            <div>
              <p className="text-white font-medium">{apiKey.name}</p>
              <p className="text-gray-500 text-xs font-mono">{apiKey.maskedKey}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-4">
          <span className={`px-2 py-1 rounded-full text-xs ${apiKey.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {apiKey.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td className="px-4 py-4">
          <div className="flex flex-col">
            <span className={`font-medium ${usageColor}`}>
              {apiKey.tokensUsed.toLocaleString()} / {apiKey.tokenLimit.toLocaleString()}
            </span>
            <div className="w-24 h-1.5 bg-gray-800 rounded-full mt-1">
              <div 
                className={`h-full rounded-full ${apiKey.usagePercentage > 80 ? 'bg-red-500' : apiKey.usagePercentage > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(apiKey.usagePercentage, 100)}%` }}
              />
            </div>
          </div>
        </td>
        <td className="px-4 py-4 text-gray-300">{apiKey.totalRequests.toLocaleString()}</td>
        <td className="px-4 py-4 text-gray-400 text-sm">{format(new Date(apiKey.createdAt), 'MMM d, yyyy')}</td>
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => onViewLogs(apiKey.id)}
              className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors text-blue-400"
              title="View Logs"
            >
              <FileText className="w-4 h-4" />
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowActions(!showActions)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-400" />
              </button>
              
              <AnimatePresence>
                {showActions && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute right-0 top-full mt-1 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-50 py-2 min-w-[160px]"
                  >
                    <button 
                      onClick={() => { onToggle(apiKey.id); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/5 flex items-center gap-2 text-gray-300"
                    >
                      {apiKey.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {apiKey.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      onClick={() => { setShowLimitModal(true); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/5 flex items-center gap-2 text-gray-300"
                    >
                      <Settings className="w-4 h-4" />
                      Set Limit
                    </button>
                    <button 
                      onClick={() => { onReset(apiKey.id); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/5 flex items-center gap-2 text-gray-300"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset Usage
                    </button>
                    <hr className="my-2 border-white/10" />
                    <button 
                      onClick={() => { onDelete(apiKey.id); setShowActions(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-red-500/10 text-red-400 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </td>
      </motion.tr>

      {/* Limit Modal */}
      <AnimatePresence>
        {showLimitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowLimitModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Set Token Limit</h3>
              <p className="text-gray-400 text-sm mb-4">Current: {apiKey.tokenLimit.toLocaleString()} tokens</p>
              <input
                type="number"
                value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 text-white mb-4"
                placeholder="New token limit"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLimitModal(false)}
                  className="flex-1 py-3 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { onUpdateLimit(apiKey.id, newLimit); setShowLimitModal(false); }}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600"
                >
                  Update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Create Key Modal
const CreateKeyModal = ({ isOpen, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tokenLimit, setTokenLimit] = useState(10000);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    const result = await onCreate(name, description, tokenLimit);
    if (result?.apiKey?.key) {
      setCreatedKey(result.apiKey.key);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setTokenLimit(10000);
    setCreatedKey(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {!createdKey ? (
          <>
            <h3 className="text-xl font-bold text-white mb-6">Create New API Key</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 text-white"
                  placeholder="Client Name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 text-white"
                  placeholder="API key description"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Token Limit</label>
                <input
                  type="number"
                  value={tokenLimit}
                  onChange={(e) => setTokenLimit(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 text-white"
                  placeholder="10000"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleClose}
                className="flex-1 py-3 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!name}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center">
              <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">API Key Created!</h3>
              <p className="text-gray-400 text-sm mb-6">Save this key securely. It won't be shown again.</p>
            </div>
            <div className="bg-black/50 rounded-xl p-4 mb-6">
              <p className="text-xs text-gray-500 mb-2">API Key</p>
              <div className="flex items-center gap-2">
                <code className="text-green-400 font-mono text-sm flex-1 break-all">{createdKey}</code>
                <button onClick={copyKey} className="p-2 hover:bg-white/10 rounded-lg">
                  {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-full py-3 rounded-xl bg-white/10 text-white hover:bg-white/20"
            >
              Done
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

// Log Detail Modal
const LogDetailModal = ({ log, onClose }) => {
  if (!log) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Request Details</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <XCircle className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Request ID</p>
              <p className="text-white font-mono text-sm break-all">{log.requestId || log.id}</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Timestamp</p>
              <p className="text-white text-sm">{format(new Date(log.timestamp), 'MMM d, yyyy HH:mm:ss')}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">API Key</p>
              <p className="text-white font-medium">{log.apiKeyName}</p>
              <p className="text-gray-500 text-xs font-mono">{log.apiKeyMasked}</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Endpoint</p>
              <code className="text-red-400 text-sm">{log.endpoint}</code>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Method</p>
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-sm font-medium">
                {log.method || 'POST'}
              </span>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                log.statusCode >= 200 && log.statusCode < 300 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {log.statusCode || 200}
              </span>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Tokens</p>
              <p className="text-white font-mono">{log.tokensConsumed?.toFixed(2)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Response Time</p>
              <p className="text-white">{log.responseTimeMs ? `${log.responseTimeMs}ms` : 'N/A'}</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">IP Address</p>
              <p className="text-white font-mono text-sm">{log.ip || 'N/A'}</p>
            </div>
          </div>

          {/* Additional Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-3">Additional Metadata</p>
              <pre className="text-gray-300 text-sm font-mono whitespace-pre-wrap bg-black/50 rounded-lg p-3 overflow-x-auto">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Admin Dashboard
const AdminApiDashboard = () => {
  const [adminKey, setAdminKey] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Tabs
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'keys', 'logs'
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [logsFilters, setLogsFilters] = useState({ apiKeyId: '', endpoint: '', startDate: '', endDate: '' });
  const [availableFilters, setAvailableFilters] = useState({ endpoints: [], apiKeys: [] });
  const [selectedLog, setSelectedLog] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bloodflow.eu';

  const fetchDashboardData = useCallback(async (key) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/keys/admin/dashboard`, {
        method: 'GET',
        headers: {
          'X-Admin-Key': key,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error('Invalid admin key');
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
      setIsAuthenticated(true);
      sessionStorage.setItem('bf_admin_key', key);
    } catch (err) {
      setError(err.message);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchLogs = useCallback(async (page = 1, filters = logsFilters) => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(filters.apiKeyId && { apiKeyId: filters.apiKeyId }),
        ...(filters.endpoint && { endpoint: filters.endpoint }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      });

      const response = await fetch(`${API_BASE_URL}/api/v1/keys/admin/logs?${params}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setLogsPagination(data.pagination);
        setAvailableFilters(data.filters);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [API_BASE_URL, adminKey, logsFilters]);

  useEffect(() => {
    const storedKey = sessionStorage.getItem('bf_admin_key');
    if (storedKey) {
      setAdminKey(storedKey);
      fetchDashboardData(storedKey);
    }
  }, [fetchDashboardData]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'logs') {
      fetchLogs(1, logsFilters);
    }
  }, [isAuthenticated, activeTab, fetchLogs, logsFilters]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (adminKey.trim()) fetchDashboardData(adminKey.trim());
  };

  const handleLogout = () => {
    sessionStorage.removeItem('bf_admin_key');
    setAdminKey('');
    setDashboardData(null);
    setIsAuthenticated(false);
  };

  const handleToggleKey = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/v1/keys/admin/${id}/toggle`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey }
      });
      fetchDashboardData(adminKey);
    } catch (err) {
      console.error('Error toggling key:', err);
    }
  };

  const handleResetKey = async (id) => {
    if (!window.confirm('Are you sure you want to reset this key\'s usage to 0?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/v1/keys/admin/${id}/reset`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey }
      });
      fetchDashboardData(adminKey);
    } catch (err) {
      console.error('Error resetting key:', err);
    }
  };

  const handleUpdateLimit = async (id, newLimit) => {
    try {
      await fetch(`${API_BASE_URL}/api/v1/keys/admin/${id}/limit`, {
        method: 'PATCH',
        headers: { 
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tokenLimit: parseFloat(newLimit) })
      });
      fetchDashboardData(adminKey);
    } catch (err) {
      console.error('Error updating limit:', err);
    }
  };

  const handleDeleteKey = async (id) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this API key? This cannot be undone.')) return;
    try {
      await fetch(`${API_BASE_URL}/api/v1/keys/admin/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      fetchDashboardData(adminKey);
    } catch (err) {
      console.error('Error deleting key:', err);
    }
  };

  const handleCreateKey = async (name, description, tokenLimit) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/keys/admin/create`, {
        method: 'POST',
        headers: { 
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, description, tokenLimit: parseFloat(tokenLimit) })
      });
      const data = await response.json();
      fetchDashboardData(adminKey);
      return data;
    } catch (err) {
      console.error('Error creating key:', err);
      return null;
    }
  };

  const handleViewKeyLogs = (keyId) => {
    setLogsFilters({ ...logsFilters, apiKeyId: keyId });
    setActiveTab('logs');
  };

  const filteredKeys = dashboardData?.apiKeys?.filter(key => 
    key.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    key.maskedKey.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden font-sans">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-600/5 rounded-full blur-[120px]" />
        <ParticleField />

        <div className="relative min-h-screen flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="w-full max-w-md"
          >
            <motion.div className="flex justify-center mb-8">
              <img src={BloodFlowLogo} alt="BloodFlow" className="h-20 w-auto" />
            </motion.div>

            <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-8 border border-white/5 shadow-2xl">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
                <p className="text-gray-400 text-sm">Enter admin key to manage API keys</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Admin Key</label>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="password"
                      value={adminKey}
                      onChange={(e) => setAdminKey(e.target.value)}
                      placeholder="Enter admin key"
                      className="w-full bg-black/50 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white placeholder-gray-600 font-mono text-sm focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20"
                      autoComplete="off"
                    />
                  </div>
                </div>
                
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4"
                    >
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <p className="text-red-300 text-sm">{error}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <motion.button 
                  type="submit"
                  disabled={isLoading || !adminKey.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 disabled:from-gray-700 disabled:to-gray-600 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2"
                >
                  {isLoading ? 'Verifying...' : <>Access Admin <ChevronRight className="w-5 h-5" /></>}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading && !dashboardData) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-sans">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-3 border-red-500/30 border-t-red-500 rounded-full mx-auto mb-4"
            style={{ borderWidth: '3px' }}
          />
          <p className="text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const { overview, requests, statistics } = dashboardData || {};

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
      <div className="fixed top-0 right-0 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[150px]" />
      <div className="fixed bottom-0 left-0 w-[400px] h-[400px] bg-red-600/5 rounded-full blur-[120px]" />

      <div className="relative">
        {/* Header */}
        <header className="border-b border-white/5 bg-black/50 backdrop-blur-xl sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src={BloodFlowLogo} alt="BloodFlow" className="h-10 w-auto" />
                <div className="hidden md:block">
                  <h1 className="text-lg font-semibold text-white">Admin Dashboard</h1>
                  <p className="text-xs text-gray-500">API Key Management</p>
                </div>
              </div>
              
              {/* Tabs */}
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                {['overview', 'keys', 'logs'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab 
                        ? 'bg-red-500 text-white' 
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => fetchDashboardData(adminKey)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                <motion.button 
                  onClick={handleLogout}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </motion.button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <StatCard icon={Key} title="Total API Keys" value={overview?.totalApiKeys || 0} color="purple" />
                <StatCard icon={CheckCircle2} title="Active Keys" value={overview?.activeApiKeys || 0} color="green" />
                <StatCard icon={XCircle} title="Inactive Keys" value={overview?.inactiveApiKeys || 0} color="red" />
                <StatCard icon={Clock} title="Requests (24h)" value={requests?.last24Hours?.toLocaleString() || 0} color="blue" />
                <StatCard icon={Activity} title="Requests (7d)" value={requests?.last7Days?.toLocaleString() || 0} color="amber" />
                <StatCard icon={Database} title="Requests (30d)" value={requests?.last30Days?.toLocaleString() || 0} color="red" />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Usage Over Time */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5"
                >
                  <h3 className="text-lg font-semibold text-white mb-4">Usage Over Time (30 Days)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={statistics?.usageByDay || []}>
                      <defs>
                        <linearGradient id="adminTokenGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'MM/dd')} stroke="#4b5563" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="#4b5563" fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip labelFormatter={(d) => format(new Date(d), 'MMM d, yyyy')} contentStyle={{ backgroundColor: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} />
                      <Area type="monotone" dataKey="requests" stroke="#ef4444" strokeWidth={2} fill="url(#adminTokenGradient)" name="Requests" />
                    </AreaChart>
                  </ResponsiveContainer>
                </motion.div>

                {/* Usage by Key */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5"
                >
                  <h3 className="text-lg font-semibold text-white mb-4">Usage by API Key</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={statistics?.usageByKey || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="requests"
                        nameKey="keyName"
                        label={({ keyName, percent }) => `${keyName?.substring(0, 10)}${keyName?.length > 10 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {statistics?.usageByKey?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </motion.div>
              </div>

              {/* Recent Requests Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden"
              >
                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Recent Requests</h3>
                  <button
                    onClick={() => setActiveTab('logs')}
                    className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
                  >
                    View All Logs <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5 text-left">
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">API Key</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recentRequests?.slice(0, 10).map((req, i) => (
                        <motion.tr 
                          key={req.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.02 }}
                          className="border-b border-white/5 hover:bg-white/5"
                        >
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {format(new Date(req.timestamp), 'MMM d, HH:mm:ss')}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-white">{req.apiKeyName}</span>
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">{req.endpoint}</code>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300 font-mono">{req.tokensConsumed.toFixed(2)}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </>
          )}

          {/* Keys Tab */}
          {activeTab === 'keys' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden"
            >
              <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-lg font-semibold text-white">API Keys</h3>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search keys..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-black/50 border border-white/10 rounded-lg py-2 pl-10 pr-4 text-sm text-white placeholder-gray-500 w-48"
                    />
                  </div>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Create Key
                  </button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 text-left">
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name / Key</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Usage</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Requests</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKeys.map((key) => (
                      <ApiKeyRow
                        key={key.id}
                        apiKey={key}
                        onToggle={handleToggleKey}
                        onReset={handleResetKey}
                        onUpdateLimit={handleUpdateLimit}
                        onDelete={handleDeleteKey}
                        onViewLogs={handleViewKeyLogs}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Filters */}
              <div className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5">
                <h3 className="text-lg font-semibold text-white mb-4">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">API Key</label>
                    <select
                      value={logsFilters.apiKeyId}
                      onChange={(e) => setLogsFilters({ ...logsFilters, apiKeyId: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded-lg py-2 px-3 text-white text-sm"
                    >
                      <option value="">All Keys</option>
                      {availableFilters.apiKeys?.map(key => (
                        <option key={key.id} value={key.id}>{key.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Endpoint</label>
                    <select
                      value={logsFilters.endpoint}
                      onChange={(e) => setLogsFilters({ ...logsFilters, endpoint: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded-lg py-2 px-3 text-white text-sm"
                    >
                      <option value="">All Endpoints</option>
                      {availableFilters.endpoints?.map(ep => (
                        <option key={ep} value={ep}>{ep}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={logsFilters.startDate}
                      onChange={(e) => setLogsFilters({ ...logsFilters, startDate: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded-lg py-2 px-3 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">End Date</label>
                    <input
                      type="date"
                      value={logsFilters.endDate}
                      onChange={(e) => setLogsFilters({ ...logsFilters, endDate: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded-lg py-2 px-3 text-white text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => fetchLogs(1, logsFilters)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium"
                  >
                    Apply Filters
                  </button>
                  <button
                    onClick={() => {
                      setLogsFilters({ apiKeyId: '', endpoint: '', startDate: '', endDate: '' });
                      fetchLogs(1, { apiKeyId: '', endpoint: '', startDate: '', endDate: '' });
                    }}
                    className="px-4 py-2 border border-white/10 rounded-lg text-gray-300 hover:bg-white/5 text-sm"
                  >
                    Clear
                  </button>
                  <span className="text-gray-500 text-sm ml-auto">
                    {logsPagination.totalCount.toLocaleString()} total records
                  </span>
                </div>
              </div>

              {/* Logs Table */}
              <div className="bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5 text-left">
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">API Key</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tokens</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logsLoading ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                            <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                            Loading logs...
                          </td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                            No logs found
                          </td>
                        </tr>
                      ) : (
                        logs.map((log, i) => (
                          <motion.tr 
                            key={log.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.01 }}
                            className="border-b border-white/5 hover:bg-white/5"
                          >
                            <td className="px-4 py-3 text-sm text-gray-300">
                              {format(new Date(log.timestamp), 'MMM d, HH:mm:ss')}
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm text-white">{log.apiKeyName}</p>
                                <p className="text-xs text-gray-500 font-mono">{log.apiKeyMasked}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <code className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">
                                {log.endpoint}
                              </code>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                log.statusCode >= 200 && log.statusCode < 300 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {log.statusCode || 200}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {log.responseTimeMs ? `${log.responseTimeMs}ms` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                              {log.tokensConsumed?.toFixed(2)}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setSelectedLog(log)}
                                className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </motion.tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {logsPagination.totalPages > 1 && (
                  <div className="p-4 border-t border-white/5 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Page {logsPagination.page} of {logsPagination.totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fetchLogs(logsPagination.page - 1, logsFilters)}
                        disabled={!logsPagination.hasPrev}
                        className="p-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => fetchLogs(logsPagination.page + 1, logsFilters)}
                        disabled={!logsPagination.hasNext}
                        className="p-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </main>
      </div>

      {/* Modals */}
      <CreateKeyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateKey}
      />

      <AnimatePresence>
        {selectedLog && (
          <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApiDashboard;
